import { useCallback, useMemo, useState } from 'react';
import type { UserProfile, UserPreferencesPatch } from '../types';
import {
  CUSTOM_PRACTICE_MAX_KEYS,
  CUSTOM_PRACTICE_MIN_KEYS,
  customPracticeAllowsDictation,
  sanitizeCustomPracticeKeys,
  selectablePracticeKeys,
} from '../lib/customPractice';
import { formatKeyLabel } from '../lib/typingProfile';
import { useEnterAction } from '../lib/useEnterAction';
import SelectableKeyboard from './SelectableKeyboard';

interface CustomPracticeSetupProps {
  user: UserProfile;
  onContinue: (keys: string[]) => void;
  onCancel: () => void;
  onPreferencesChange?: (patch: UserPreferencesPatch) => void;
}

export default function CustomPracticeSetup({
  user,
  onContinue,
  onCancel,
  onPreferencesChange,
}: CustomPracticeSetupProps) {
  const allowed = useMemo(() => selectablePracticeKeys(user), [user]);
  const initial = useMemo(
    () =>
      sanitizeCustomPracticeKeys(user, user.preferences?.customPracticeKeys),
    [user],
  );

  const [selected, setSelected] = useState<string[]>(() =>
    initial.length > 0 ? initial : [],
  );

  const toggleKey = useCallback((key: string) => {
    setSelected((prev) => {
      if (prev.includes(key)) {
        return prev.filter((k) => k !== key);
      }
      if (prev.length >= CUSTOM_PRACTICE_MAX_KEYS) return prev;
      return [...prev, key];
    });
  }, []);

  const canStart =
    selected.length >= CUSTOM_PRACTICE_MIN_KEYS &&
    selected.length <= CUSTOM_PRACTICE_MAX_KEYS;

  const handleStart = useCallback(() => {
    if (!canStart) return;
    const keys = sanitizeCustomPracticeKeys(user, selected);
    if (keys.length < CUSTOM_PRACTICE_MIN_KEYS) return;
    onPreferencesChange?.({ customPracticeKeys: keys });
    onContinue(keys);
  }, [canStart, user, selected, onPreferencesChange, onContinue]);

  useEnterAction(handleStart, canStart);

  const dictationNote = customPracticeAllowsDictation(selected)
    ? 'Inkluderer ord-diktat til sidst.'
    : 'Uden diktat — komma, punktum og bindestreg kan ikke høres tydeligt.';

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 animate-fade-in-up">
      <header>
        <p className="label">Ekstra øvelse</p>
        <h1 className="mt-1 text-2xl font-semibold text-strong">Vælg selv</h1>
        <p className="mt-2 text-sm text-muted">
          Kryds 1–5 taster af du vil øve. Valget huskes til næste gang.{' '}
          {dictationNote}
        </p>
      </header>

      <section className="surface p-5">
        <p className="label">Dine taster</p>
        <p className="mt-1 text-xs text-faint">
          {selected.length}/{CUSTOM_PRACTICE_MAX_KEYS} valgt
          {selected.length > 0 &&
            ` — ${selected.map((k) => formatKeyLabel(k)).join(', ')}`}
        </p>
        <div className="mt-4">
          <SelectableKeyboard
            layoutId={user.keyboardLayout}
            unlockedKeys={allowed}
            selectedKeys={selected}
            maxSelected={CUSTOM_PRACTICE_MAX_KEYS}
            onToggleKey={toggleKey}
          />
        </div>
      </section>

      <div className="flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          onClick={handleStart}
          disabled={!canStart}
          className="flex-1 rounded-xl bg-indigo-500 px-4 py-3 font-semibold text-white shadow-sm transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50 dark:shadow-lg dark:shadow-indigo-500/30"
        >
          Start træning
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary flex-1">
          Tilbage
        </button>
      </div>
      <p className="text-center text-xs text-faint">
        {canStart
          ? 'Tryk Enter for at starte'
          : `Vælg mindst ${CUSTOM_PRACTICE_MIN_KEYS} tast`}
      </p>
    </div>
  );
}
